import _ from 'lodash';

import {atIndex} from '../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {
  extractInitials,
  isNonEmptyString,
} from '../../../libs/shared/core/src/helpers/utilities/stringHelpers';
import {ContactModel, ContactSourcesEnum} from '../../../libs/shared/core/src/models/api/contactModel';
import {TeammateModel} from '../../../libs/shared/core/src/models/api/teammateModel';
import {globalNamespace} from '../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {buildTeammateDisplayName} from '../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {ResourceMap} from '../../../libs/shared/core/src/store/commonAppTypes';
import {CardModel, CardTypesEnum} from '../../models/api/cardModel';
import {CrmSyncModel} from '../../models/api/crmSyncModel';
import {VisualContactModel} from '../../models/api/visualContactModel';
import {getOnlineStatusForTeammate} from './teammateHelpers';

export function findCardInVisualContact(visualContact: VisualContactModel) {
  return visualContact.card || visualContact.fallbackCard;
}

export function isContactACustomChannel(contact: ContactModel) {
  return contact.source === ContactSourcesEnum.CUSTOM && Boolean(contact.channelId);
}

// Helper to bridge the gap between global cards and other scenarios that use global cards
// but not in a global namespace, e.g. in team conversations
export function findCardForContactInNamespace(
  contact: ContactModel,
  cards: ResourceMap<CardModel>,
  namespace: string,
) {
  // Prioritize company cards (global namespace, enhanced type)
  const cardIdForGlobalNamespace = contact.cardIds[globalNamespace];
  const cardForGlobalNamespace = cardIdForGlobalNamespace ? cards[cardIdForGlobalNamespace] : undefined;
  if (cardForGlobalNamespace?.type === CardTypesEnum.ENHANCED) {
    return cardForGlobalNamespace;
  }

  // Then, if a card for the given namespace exists, return that card
  // After we migrate entirely to company cards and remove team cards, this will only apply to private namespaces
  const cardIdForNamespace = contact.cardIds[namespace];
  const cardForNamespace = cardIdForNamespace ? cards[cardIdForNamespace] : undefined;
  if (cardForNamespace) {
    return cardForNamespace;
  }

  // Otherwise, return the auto-created card, if any
  if (cardForGlobalNamespace?.type === CardTypesEnum.AUTO) {
    return cardForGlobalNamespace;
  }

  return undefined;
}

export function buildCardCrmExternalUrl(card: CardModel, crmSync: CrmSyncModel) {
  if (!card.externalInfo || !crmSync.externalUrl) {
    return undefined;
  }
  if (card.externalInfo.source !== crmSync.source) {
    return undefined;
  }
  return `${crmSync.externalUrl}/${card.externalInfo.id}`;
}

/*
 * Display.
 */

/** Compute the handle to display for the specified visual contact. */
export function buildContactDisplayHandle(visualContact: VisualContactModel) {
  // Give priority to the fallback contact.
  const {fallbackContact} = visualContact;
  if (fallbackContact) {
    return fallbackContact.handle;
  }

  // Otherwise, use the recipient handle.
  return visualContact.contact.handle;
}

/** Compute the name to display for the specified visual contact. */
export function buildContactDisplayName(visualContact: VisualContactModel) {
  // #1. User-specified card name.
  const editedCardName = tryFindContactCardName(visualContact, {requireNameEdited: true});
  if (editedCardName) {
    return editedCardName;
  }

  // #2. Teammate name.
  if (visualContact.teammate) {
    return buildTeammateDisplayName(visualContact.teammate);
  }

  // #3. Recipient name.
  if (visualContact.name) {
    return visualContact.name;
  }

  // #4. Auto-generated card name.
  const cardName = tryFindContactCardName(visualContact);
  if (cardName) {
    return cardName;
  }

  // #5. Handle.
  return buildContactDisplayHandle(visualContact);
}

function tryFindContactCardName(
  visualContact: VisualContactModel,
  {requireNameEdited}: {requireNameEdited?: boolean} = {},
) {
  const {card, fallbackCard} = visualContact;

  if (card && card.name && (card.wasNameEdited || !requireNameEdited)) {
    return card.name;
  }

  if (fallbackCard && fallbackCard.name && (fallbackCard.wasNameEdited || !requireNameEdited)) {
    return fallbackCard.name;
  }

  return undefined;
}

/** Compute the initials to display for the specified visual contact. */
export function buildContactInitials(visualContact: VisualContactModel) {
  // Build the name, and extract the initials.
  const name = buildContactDisplayName(visualContact);
  return extractInitials(name);
}

/** Compute the avatar URL for the specified visual contact. */
export function buildContactAvatarUrl(visualContact: VisualContactModel) {
  // If we have a cad, that takes precedence.
  const {card, fallbackCard, teammate} = visualContact;
  if (card && card.avatarUrl) {
    return card.avatarUrl;
  }

  // Then, check for a fallback card.
  if (fallbackCard && fallbackCard.avatarUrl) {
    return fallbackCard.avatarUrl;
  }

  // If this is a teammate, use its avatarUrl
  if (teammate && teammate.avatarUrl) {
    return teammate.avatarUrl;
  }

  // If none was found, use the contact.
  return visualContact.contact && visualContact.contact.avatarUrl;
}

/** Compute the string representation of the specified visual contact. */
export function buildContactString(visualContact: VisualContactModel) {
  const handle = buildContactDisplayHandle(visualContact);
  const name = buildContactDisplayName(visualContact);

  // If the handle and name are the same, return just one.
  if (handle === name) {
    return handle;
  }

  // Otherwise, return both.
  return `${name} <${handle}>`;
}

/** Filters contacts that are connected to any card in the array passed in. */
export function filterContactsConnectedToCardIds(
  contacts: ReadonlyArray<ContactModel>,
  cardIds: ReadonlyArray<number>,
) {
  return _(contacts)
    .filter((contact) =>
      _(contact.cardIds)
        .values()
        .compact()
        .value()
        .some((val) => cardIds.includes(val)),
    )
    .value();
}

/** Removes any card ids passed in from the contact. */
export function removeCardIdsFromContact(
  contact: ContactModel,
  cardIds: ReadonlyArray<number>,
): ContactModel {
  return {
    ...contact,
    cardIds: _(contact.cardIds).reduce(
      (result, cardId, namespace) => ({
        ...result,
        [namespace]: !cardId || cardIds.includes(cardId) ? undefined : cardId,
      }),
      {},
    ),
  };
}

/*
 * Contact to Visual Contact Helpers.
 */

interface CreateVisualContactModelOptions {
  contact: ContactModel;
  allCards?: ResourceMap<CardModel>;
  selectedCard?: CardModel;
  namespace: string;
  name: string | undefined;
  fallbackAvatarUrl?: string;
  teammate: TeammateModel | undefined;
  onlineTeammateIds?: ReadonlySet<number>;

  shouldAllowContactsWithoutUrls?: boolean;
}

export function createVisualContactModel({
  contact,
  namespace,
  allCards,
  selectedCard,
  name,
  fallbackAvatarUrl,
  teammate,
  onlineTeammateIds,
  shouldAllowContactsWithoutUrls,
}: CreateVisualContactModelOptions): VisualContactModel | undefined {
  // If the contact has no url, we won't be able to load its related cards and other contacts to serve meaningful content.
  if (!contact.url && !shouldAllowContactsWithoutUrls) {
    return undefined;
  }

  const updatedContact = {
    ...contact,

    avatarUrl: contact.avatarUrl || fallbackAvatarUrl,
  };

  const cards = selectedCard
    ? [selectedCard]
    : _(contact.cardIds)
        .map((cardId) => cardId && allCards && allCards[cardId])
        .compact()
        .value();

  const card = selectedCard ?? findCardForContactInNamespace(contact, allCards ?? {}, namespace);

  let displayName = name;

  if (!displayName || displayName.includes('@')) {
    const cardNames = cards.map((c) => c.name).filter(isNonEmptyString);
    const goodName = cardNames.find((n) => !n.includes('@'));

    if (goodName) {
      displayName = goodName;
    } else if (!displayName) {
      displayName = atIndex(cardNames, 0) || contact.handle;
    }
  }

  const onlineStatus = getOnlineStatusForTeammate(teammate, onlineTeammateIds);

  const visualContact: VisualContactModel = {
    name: displayName,
    card,
    contact: updatedContact,
    namespace,
    teammate,
    onlineStatus,
    isCardDenied: card?.isDenied,
  };

  return visualContact;
}
